import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useLang } from '../../i18n/LanguageContext';

import API_BASE_URL from '../../config';

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) > 1 ? 's' : ''} ago`;
}

function deviceIcon(os) {
  if (!os) return 'bi-display';
  const o = os.toLowerCase();
  if (o.includes('iphone') || o.includes('android')) return 'bi-phone';
  if (o.includes('ipad'))  return 'bi-tablet';
  if (o.includes('mac'))   return 'bi-laptop';
  return 'bi-display';
}

export default function Settings({ showNotification }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ownerUser') || '{}'); } catch { return {}; }
  });

  // Change password — step: 1=send otp, 2=verify otp, 3=new password
  const [pwStep, setPwStep] = useState(1);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const timerRef = useRef(null);

  // Notification preferences
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('soundEnabled') !== 'false');
  const [refreshInterval, setRefreshInterval] = useState(() => parseInt(localStorage.getItem('refreshInterval') || '5000'));

  // Logged-in devices
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revoking, setRevoking] = useState(null); // session id being revoked

  const fetchSessions = useCallback(async () => {
    const token = localStorage.getItem('ownerToken');
    if (!token) return;
    setSessionsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSessions(res.data.data);
        setCurrentSessionId(res.data.currentSessionId);
      }
    } catch {
      // silently fail — old tokens without sessionId just show empty list
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleRevokeSession = async (sessionId) => {
    const token = localStorage.getItem('ownerToken');
    setRevoking(sessionId);
    try {
      await axios.delete(`${API_BASE_URL}/auth/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      showNotification?.('Device logged out', 'success');
    } catch {
      showNotification?.('Failed to log out device', 'error');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeOthers = async () => {
    const token = localStorage.getItem('ownerToken');
    setRevoking('others');
    try {
      const res = await axios.delete(`${API_BASE_URL}/auth/sessions/others`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification?.(res.data.message || 'All other devices logged out', 'success');
      fetchSessions();
    } catch {
      showNotification?.('Failed to log out other devices', 'error');
    } finally {
      setRevoking(null);
    }
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startTimer = () => {
    setOtpTimer(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const resetPwForm = () => {
    setPwStep(1);
    setPwError('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setOtpTimer(0);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleSendOTP = async () => {
    setPwError('');
    setPwLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/request-otp`, { email: user.email });
      setPwStep(2);
      startTimer();
      showNotification?.('Verification code sent to your email!', 'success');
    } catch (err) {
      setPwError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setPwLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) { setPwError('Please enter the 6-digit code from your email'); return; }
    setPwError('');
    setPwLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/verify-otp`, { email: user.email, otp });
      setPwStep(3);
      showNotification?.('Code verified! Set your new password.', 'success');
    } catch (err) {
      setPwError(err.response?.data?.message || 'Invalid or expired code. Try again.');
    } finally {
      setPwLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) { setPwError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setPwError('Passwords do not match'); return; }
    setPwError('');
    setPwLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password`, { email: user.email, otp, newPassword });
      showNotification?.('Password changed successfully! Please sign in again.', 'success');
      setTimeout(() => {
        localStorage.removeItem('ownerToken');
        localStorage.removeItem('ownerUser');
        navigate('/login');
      }, 2000);
    } catch (err) {
      setPwError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setPwLoading(false);
    }
  };

  const savePreferences = () => {
    localStorage.setItem('soundEnabled', soundEnabled);
    localStorage.setItem('refreshInterval', refreshInterval);
    showNotification?.('Preferences saved!', 'success');
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h2 className="page-title"><i className="bi bi-gear-fill me-2"></i>{t('settings.title')}</h2>
          <p className="page-subtitle">{t('settings.subtitle')}</p>
        </div>
      </div>

      <div className="settings-grid">
        {/* Account Info */}
        <motion.div className="settings-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <h5 className="settings-card-title"><i className="bi bi-person-circle me-2"></i>{t('settings.account')}</h5>
          <div className="settings-info-row">
            <span className="settings-info-label">{t('settings.email')}</span>
            <span className="settings-info-value">{user.email || '—'}</span>
          </div>
          <div className="settings-info-row">
            <span className="settings-info-label">{t('settings.role')}</span>
            <span className="settings-info-value badge-owner">{t('settings.owner')}</span>
          </div>
        </motion.div>

        {/* Change Password */}
        <motion.div className="settings-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h5 className="settings-card-title"><i className="bi bi-shield-lock me-2"></i>{t('settings.changePassword')}</h5>

          {/* Step indicator */}
          <div className="step-indicator" style={{ marginBottom: '1.25rem' }}>
            {[1, 2, 3].map((s, i) => (
              <span key={s} style={{ display: 'flex', alignItems: 'center' }}>
                <span className={`step-dot${pwStep >= s ? ' active' : ''}`}>{s}</span>
                {i < 2 && <span className={`step-line${pwStep > s ? ' active' : ''}`}></span>}
              </span>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1 — Send OTP */}
            {pwStep === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <p className="settings-desc">We'll send a verification code to <strong>{user.email}</strong></p>
                {pwError && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{pwError}</div>}
                <button className="btn-primary-action" onClick={handleSendOTP} disabled={pwLoading}>
                  {pwLoading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                    : <><i className="bi bi-envelope me-2"></i>Send Verification Code</>}
                </button>
              </motion.div>
            )}

            {/* Step 2 — Enter OTP */}
            {pwStep === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <p className="settings-desc">Enter the 6-digit code sent to <strong>{user.email}</strong></p>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-input otp-input"
                    placeholder="000000"
                    maxLength={6}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\s/g, ''))}
                    style={{ letterSpacing: 6, textAlign: 'center', fontSize: '1.5rem' }}
                    autoFocus
                  />
                </div>
                {otpTimer > 0
                  ? <p className="settings-desc" style={{ color: '#a05a2c', marginBottom: '0.75rem' }}>Resend code in {otpTimer}s</p>
                  : <button className="btn-link-action" onClick={handleSendOTP} disabled={pwLoading} style={{ marginBottom: '0.75rem' }}>Resend Code</button>
                }
                {pwError && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{pwError}</div>}
                <div className="form-actions">
                  <button className="btn-cancel-form" onClick={resetPwForm}>Back</button>
                  <button className="btn-submit-form" onClick={handleVerifyOTP} disabled={pwLoading || otp.length !== 6}>
                    {pwLoading
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying...</>
                      : <><i className="bi bi-check-lg me-2"></i>Verify Code</>}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3 — New Password */}
            {pwStep === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <p className="settings-desc">Create a new password for your account.</p>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder="Min 6 characters"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#8B4513', padding: 0 }}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />
                </div>
                {pwError && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{pwError}</div>}
                <div className="form-actions">
                  <button className="btn-cancel-form" onClick={resetPwForm}>Cancel</button>
                  <button className="btn-submit-form" onClick={handleChangePassword} disabled={pwLoading || !newPassword || !confirmPassword}>
                    {pwLoading
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                      : <><i className="bi bi-save me-2"></i>Update Password</>}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Notification Preferences */}
        <motion.div className="settings-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h5 className="settings-card-title"><i className="bi bi-bell me-2"></i>{t('settings.notificationPrefs')}</h5>

          <div className="settings-pref-row">
            <div>
              <p className="settings-pref-label">{t('settings.sound')}</p>
              <p className="settings-pref-desc">{t('settings.soundSub')}</p>
            </div>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="soundToggle"
                checked={soundEnabled}
                onChange={e => setSoundEnabled(e.target.checked)}
              />
            </div>
          </div>

          <div className="settings-pref-row">
            <div>
              <p className="settings-pref-label">{t('settings.pollingInterval')}</p>
              <p className="settings-pref-desc">{t('settings.pollingSub')}</p>
            </div>
            <select
              className="form-select-input"
              style={{ width: 120 }}
              value={refreshInterval}
              onChange={e => setRefreshInterval(parseInt(e.target.value))}
            >
              <option value={5000}>5 seconds</option>
              <option value={10000}>10 seconds</option>
              <option value={30000}>30 seconds</option>
            </select>
          </div>

          <button className="btn-primary-action mt-3" onClick={savePreferences}>
            <i className="bi bi-save me-2"></i>{t('settings.savePreferences')}
          </button>
        </motion.div>
        {/* Logged In Devices */}
        <motion.div className="settings-card settings-card-full" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <div className="devices-header">
            <div>
              <h5 className="settings-card-title" style={{ marginBottom: 2 }}>
                <i className="bi bi-shield-check me-2"></i>Logged In Devices
              </h5>
              <p className="settings-desc" style={{ marginBottom: 0 }}>
                These devices are currently signed in to your dashboard.
              </p>
            </div>
            {sessions.length > 1 && (
              <button
                className="btn-danger-action"
                onClick={handleRevokeOthers}
                disabled={revoking === 'others'}
              >
                {revoking === 'others'
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Logging out...</>
                  : <><i className="bi bi-box-arrow-right me-2"></i>Log Out All Other Devices</>}
              </button>
            )}
          </div>

          {sessionsLoading ? (
            <div className="devices-skeleton">
              {[1, 2].map(i => <div key={i} className="device-skeleton-row" />)}
            </div>
          ) : sessions.length === 0 ? (
            <div className="devices-empty">
              <i className="bi bi-info-circle me-2"></i>
              No active sessions found. Log out and log back in to see your devices here.
            </div>
          ) : (
            <div className="devices-list">
              {sessions.map(s => {
                const isCurrent = s.sessionId === currentSessionId;
                return (
                  <div key={s.id} className={`device-row${isCurrent ? ' device-row-current' : ''}`}>
                    <div className="device-icon-wrap">
                      <i className={`bi ${deviceIcon(s.os)}`}></i>
                    </div>
                    <div className="device-info">
                      <div className="device-name">
                        {s.deviceName}
                        {isCurrent && <span className="badge-current">This device</span>}
                      </div>
                      <div className="device-meta">
                        {s.ipAddress && <span><i className="bi bi-geo-alt me-1"></i>{s.ipAddress}</span>}
                        <span><i className="bi bi-clock me-1"></i>{timeAgo(s.lastActive)}</span>
                        <span><i className="bi bi-calendar3 me-1"></i>Signed in {timeAgo(s.createdAt)}</span>
                      </div>
                    </div>
                    {!isCurrent && (
                      <button
                        className="btn-logout-device"
                        onClick={() => handleRevokeSession(s.id)}
                        disabled={revoking === s.id}
                        title="Log out this device"
                      >
                        {revoking === s.id
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : <><i className="bi bi-box-arrow-right me-1"></i>Log Out</>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

      </div>
    </div>
  );
}
